import { cn } from '@/lib/utils';
import { Bell, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';
import NotificationItem from './NotificationItem';

const NotificationList = ({ notifications, loading, onMarkAsRead, unreadOnly, hasMore, onLoadMore, searchQuery }) => {
   const observerRef = useRef();
   const loadingRef = useRef(null);

   const handleObserver = useCallback((entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loading) {
         onLoadMore();
      }
   }, [hasMore, loading, onLoadMore]);

   // Setup observer
   useEffect(() => {
      const currentLoader = loadingRef.current;

      if (!currentLoader) return;

      const options = {
         root: null,
         rootMargin: '20px',
         threshold: 0.1
      };

      const observer = new IntersectionObserver(handleObserver, options);
      observer.observe(currentLoader);

      return () => {
         if (currentLoader) {
            observer.unobserve(currentLoader);
         }
         observer.disconnect();
      };
   }, [handleObserver, notifications]);

   // Reset scroll position when notifications change
   useEffect(() => {
      const container = document.querySelector('.notifications-container');
      if (container) {
         container.scrollTop = 0;
      }
   }, [unreadOnly]); // Reset scroll when switching tabs

   return (
      <div className="notifications-container h-[calc(900px-132px)] overflow-y-auto px-2 pr-2 mt-2 mr-4 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
         <div className="space-y-2 py-2 max-w-4xl mx-auto">
            {notifications?.length === 0 && !loading ? (
               <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <Bell className="h-12 w-12 mb-2 dark:text-gray-400" />
                  <p>No notifications</p>
               </div>
            ) : (
               <>
                  {notifications?.map((notification) => (
                     <div
                        key={notification.id}
                        onClick={() => !notification.read && onMarkAsRead(notification.id)}
                        className={cn(
                           "p-3 rounded-lg border transition-colors cursor-pointer hover:shadow-sm",
                           !notification.read && "bg-primary/5 hover:bg-primary/10",
                           notification.read && "bg-background hover:bg-accent/50"
                        )}
                     >
                        <NotificationItem
                           notification={notification}
                           searchQuery={searchQuery}
                        />
                     </div>
                  ))}
                  {(loading || hasMore) && (
                     <div
                        ref={loadingRef}
                        className="flex justify-center items-center py-2"
                     >
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                     </div>
                  )}
               </>
            )}
         </div>
      </div>
   );
};

export default NotificationList;
